/**
 * Getting photo PIXELS onto the screen (M5 / S05-04).
 *
 * Two sources, and the difference matters offline:
 *
 *   LOCAL — a staged photo's verified-clean bytes are already on the device.
 *   They render with no network at all, which is why a photo Brandon just took
 *   in a basement appears immediately and keeps appearing while it waits to
 *   upload.
 *
 *   SERVER — a delivered photo's local bytes were deleted after the server
 *   confirmed the archive (D-028), so its pixels come from Storage. The bucket
 *   is PRIVATE and has no public URL: the only read path is a signed URL,
 *   issued to the owner by policies that key on `auth.uid()`.
 *
 * **THE OBJECT PATH IS DERIVED IN ONE PLACE.** `photoObjectPath` is S05-03's,
 * and `confirm_capture_photo_archive_uploaded` derives the same shape
 * server-side from row truth. Two derivations that must agree are one drift
 * away from a viewer that shows nothing, so this module maps a row onto the
 * existing function rather than rebuilding the string.
 *
 * NOTHING HERE UPLOADS. It is a read path only.
 */

import type { CapturePhotoRow } from '../database.types';
import type { SupabaseClient } from '@supabase/supabase-js';
import { PHOTO_BUCKET, photoObjectPath } from './photo-delivery';

export type PhotoVariant = 'archive' | 'display' | 'thumb';

/** Long enough to open a photo and look at it; short enough not to linger. */
export const SIGNED_URL_TTL_SECONDS = 300;

export function serverPhotoObjectPath(row: CapturePhotoRow, variant: PhotoVariant): string {
  return photoObjectPath(
    {
      ownerUserId: row.owner_user_id,
      captureId: row.capture_id,
      clientPhotoUuid: row.client_photo_uuid,
    },
    variant,
    row.source_mime,
  );
}

export interface PhotoImageUrlPort {
  /** A time-limited read URL, or null when one could not be issued. */
  readonly signedUrl: (path: string) => Promise<string | null>;
}

export function createSupabasePhotoImageUrls(client: SupabaseClient): PhotoImageUrlPort {
  return {
    signedUrl: async (path) => {
      try {
        const { data, error } = await client.storage
          .from(PHOTO_BUCKET)
          .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
        // A failure here is an absent picture, never a broken screen: the tile
        // keeps its label and its state, which are the parts that carry
        // meaning anyway.
        if (error) return null;
        return data.signedUrl;
      } catch {
        return null;
      }
    },
  };
}

/**
 * A blob URL for bytes already on the device, or null where the runtime has no
 * `createObjectURL` (jsdom). **Callers must revoke it** — these hold the bytes
 * alive, and a photo screen creates one per tile.
 */
export function createBytesObjectUrl(bytes: Uint8Array, mime: string): string | null {
  if (typeof URL.createObjectURL !== 'function') return null;
  return URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
}

export function revokeObjectUrl(url: string | null): void {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

/* ==========================================================================
 * Orientation — the archive half of I-040
 * ========================================================================== */

/**
 * How to display the ARCHIVE upright, given the orientation recorded beside it.
 *
 * **This exists because the archive is not self-describing.** Sanitization
 * removed the EXIF block and the Orientation tag went with it (PT §9), so a
 * renderer that simply shows the archive bytes shows a sideways photo for
 * every orientation 5–8 image ever taken — with no error anywhere. The value
 * survives as `capture_photo.source_orientation`, and this is where it is
 * applied.
 *
 * **The archive is never rewritten to make this unnecessary.** Rotating and
 * re-encoding it would re-compress exactly the detail PT §9 retains it for.
 * The picture is corrected at display time; the stored bytes stay as they are.
 *
 * DERIVATIVES MUST NOT GO THROUGH HERE. `display` and `thumbnail` were built
 * upright from the original by the S05-02 pipeline, so applying orientation to
 * them would rotate an already-rotated picture — the classic double-rotation
 * bug, and the reason this function names the archive in its own name.
 *
 * The mapping is the same table `orientationTransform` uses for the canvas,
 * expressed as CSS. It is written out rather than derived because the mirrored
 * cases (2, 4, 5, 7) are exactly where a clever derivation goes wrong quietly.
 */
export function archiveOrientationTransform(orientation: number | null): string {
  switch (orientation) {
    case 2:
      return 'scaleX(-1)';
    case 3:
      return 'rotate(180deg)';
    case 4:
      return 'scaleY(-1)';
    case 5:
      return 'scaleX(-1) rotate(90deg)';
    case 6:
      return 'rotate(90deg)';
    case 7:
      return 'scaleX(-1) rotate(270deg)';
    case 8:
      return 'rotate(270deg)';
    case 1:
    default:
      // Null included: no recorded orientation means no rotation, never a
      // guessed one.
      return 'none';
  }
}

/** 5–8 exchange the axes, so an upright 4000×3000 archive presents as 3000×4000. */
export function archiveAxesSwapped(orientation: number | null): boolean {
  return orientation === 5 || orientation === 6 || orientation === 7 || orientation === 8;
}
